import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  X, Calendar, Clock, Link2, FileText, Users, Target, Upload, MessageCircleQuestion,
  UserCheck, Loader2, StickyNote, ListChecks, Lightbulb,
} from 'lucide-react';
import { API, COLUMNS, CATEGORY_META, fmtDate } from '../../lib/planConstants';

const Row = ({ icon: Icon, label, children }) => (
  <div className="flex gap-3 py-2 border-b border-slate-800/70 last:border-0">
    <Icon className="w-4 h-4 text-slate-300 mt-0.5 flex-shrink-0" />
    <div className="min-w-0 flex-1">
      <p className="text-[11px] uppercase tracking-wider text-slate-300">{label}</p>
      <div className="text-sm text-slate-200 mt-0.5">{children}</div>
    </div>
  </div>
);

export default function TaskDrawer({ task, allTasks, authHeader, onClose, onChanged, onAskCoach }) {
  const [notes, setNotes] = useState(task.notes || '');
  const [file, setFile] = useState(null);
  const [evidenceNote, setEvidenceNote] = useState('');
  const [busy, setBusy] = useState(false);
  const cat = CATEGORY_META[task.category] || CATEGORY_META.personal;
  const deps = (task.depends_on || []).map((id) => allTasks.find((t) => t.id === id)).filter(Boolean);

  const patch = async (body, msg) => {
    setBusy(true);
    try {
      const r = await axios.patch(`${API}/futurepath-plan/tasks/${task.id}`, body, authHeader);
      if (r.data.milestone_completed) toast.success(`Milestone complete: ${r.data.milestone_completed}${r.data.badge_earned ? ` · Badge earned: ${r.data.badge_earned}` : ''}`);
      else if (msg) toast.success(msg);
      if ((r.data.unblocked || []).length) toast.info(`Unlocked: ${r.data.unblocked.join(', ')}`);
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not update the task.');
    } finally {
      setBusy(false);
    }
  };

  const uploadEvidence = async () => {
    if (!file && !evidenceNote.trim()) return toast.error('Add a file or a note as evidence.');
    setBusy(true);
    try {
      const form = new FormData();
      if (file) form.append('file', file);
      form.append('note', evidenceNote);
      await axios.post(`${API}/futurepath-plan/tasks/${task.id}/evidence`, form, {
        ...authHeader,
        headers: { ...(authHeader.headers || {}), 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Evidence saved');
      setFile(null); setEvidenceNote('');
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-[#0a1729]/60" onClick={onClose} data-testid="task-drawer">
      <div className="w-full max-w-xl h-full overflow-y-auto bg-[#08203A] border-l border-cyan-500/20 p-5 sm:p-6"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <div className="flex-1">
            <span className={`text-[11px] px-2 py-0.5 rounded-full ${cat.bg} ${cat.text}`}>{cat.label}</span>
            <h2 className="text-lg md:text-lg font-semibold text-white mt-2 leading-snug">{task.title}</h2>
            {task.milestone_title && <p className="text-xs text-cyan-300/80 mt-1">Milestone · {task.milestone_title}</p>}
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white" data-testid="task-drawer-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <select value={task.status} onChange={(e) => patch({ status: e.target.value }, 'Status updated')}
            disabled={busy}
            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="task-status-select">
            {COLUMNS.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
          </select>
          <select value={task.priority || 'medium'} onChange={(e) => patch({ priority: e.target.value }, 'Priority updated')}
            disabled={busy}
            className="bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="task-priority-select">
            {['high', 'medium', 'low'].map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </div>

        <div className="mt-4">
          {task.description && <p className="text-sm text-slate-300">{task.description}</p>}
          {task.why && (
            <div className="mt-3 rounded-xl border border-cyan-500/25 bg-cyan-500/5 p-3">
              <p className="text-[11px] uppercase tracking-wider text-cyan-300 flex items-center gap-1.5">
                <Lightbulb className="w-3.5 h-3.5" /> Why this matters
              </p>
              <p className="text-sm text-slate-200 mt-1">{task.why}</p>
            </div>
          )}
        </div>

        <div className="mt-4">
          <Row icon={Calendar} label="Dates">
            <div className="flex items-center gap-2">
              <input type="date" value={(task.start_date || '').slice(0, 10)}
                onChange={(e) => patch({ start_date: e.target.value })}
                className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white"
                data-testid="task-start-date" />
              <span className="text-slate-300 text-xs">→</span>
              <input type="date" value={(task.due_date || '').slice(0, 10)}
                onChange={(e) => patch({ due_date: e.target.value }, 'Due date updated')}
                className="bg-slate-900 border border-slate-700 rounded-lg px-2 py-1 text-xs text-white"
                data-testid="task-due-date" />
            </div>
          </Row>
          {task.estimated_effort && <Row icon={Clock} label="Estimated effort">{task.estimated_effort}</Row>}
          {deps.length > 0 && (
            <Row icon={Link2} label="Depends on">
              <ul className="space-y-1">
                {deps.map((d) => (
                  <li key={d.id} className={d.status === 'completed' ? 'text-emerald-300' : 'text-amber-300'}>
                    {d.status === 'completed' ? '✓ ' : '• '}{d.title}
                  </li>
                ))}
              </ul>
            </Row>
          )}
          {(task.required_documents || []).length > 0 && (
            <Row icon={FileText} label="Required documents">{task.required_documents.join(', ')}</Row>
          )}
          {(task.resources || []).length > 0 && (
            <Row icon={ListChecks} label="Resources">
              <ul className="space-y-1">{task.resources.map((r, i) => <li key={i}>{r}</li>)}</ul>
            </Row>
          )}
          {(task.people_to_contact || []).length > 0 && (
            <Row icon={Users} label="People to contact">{task.people_to_contact.join(', ')}</Row>
          )}
          {task.completion_criteria && <Row icon={Target} label="Done when">{task.completion_criteria}</Row>}
          {task.needs_review && task.needs_review !== 'none' && (
            <Row icon={UserCheck} label="Review">
              <div className="flex items-center gap-2">
                <span className="capitalize">{task.needs_review} review · {(task.review_status || 'not_requested').replace(/_/g, ' ')}</span>
                {task.review_status !== 'requested' && (
                  <button onClick={() => patch({ review_status: 'requested' }, 'Review requested')}
                    className="text-xs text-cyan-300 hover:text-cyan-200" data-testid="request-review-btn">
                    Request review
                  </button>
                )}
              </div>
            </Row>
          )}
        </div>

        <div className="mt-5">
          <p className="text-[11px] uppercase tracking-wider text-slate-300 flex items-center gap-1.5 mb-2">
            <StickyNote className="w-3.5 h-3.5" /> My notes
          </p>
          <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
            className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            placeholder="What you tried, who you talked to, what's next…" data-testid="task-notes" />
          <button onClick={() => patch({ notes }, 'Notes saved')} disabled={busy}
            className="mt-2 text-xs text-cyan-300 hover:text-cyan-200" data-testid="save-notes-btn">Save notes</button>
        </div>

        <div className="mt-5 rounded-xl border border-slate-700/80 bg-slate-900/40 p-4">
          <p className="text-[11px] uppercase tracking-wider text-slate-300 flex items-center gap-1.5">
            <Upload className="w-3.5 h-3.5" /> Evidence
          </p>
          {task.evidence_requested && <p className="text-xs text-cyan-300/80 mt-1">Asked for: {task.evidence_requested}</p>}
          {(task.evidence || []).length > 0 && (
            <ul className="mt-2 space-y-1">
              {task.evidence.map((e) => (
                <li key={e.id} className="text-xs text-slate-300">
                  {e.filename ? `📎 ${e.filename}` : '📝'} {e.note} <span className="text-slate-300">· {fmtDate(e.uploaded_at)}</span>
                </li>
              ))}
            </ul>
          )}
          <input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="mt-3 block w-full text-xs text-slate-300" data-testid="evidence-file" />
          <input value={evidenceNote} onChange={(e) => setEvidenceNote(e.target.value)}
            placeholder="Add a note about this evidence"
            className="mt-2 w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white"
            data-testid="evidence-note" />
          <button onClick={uploadEvidence} disabled={busy}
            className="mt-2 text-xs font-semibold text-cyan-300 hover:text-cyan-200 inline-flex items-center gap-1.5"
            data-testid="upload-evidence-btn">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Save evidence
          </button>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button onClick={() => onAskCoach(task)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan-500/40 text-sm text-cyan-200 hover:bg-cyan-500/10"
            data-testid="ask-coach-btn">
            <MessageCircleQuestion className="w-4 h-4" /> Ask my AI coach about this
          </button>
          {task.status !== 'completed' && (
            <button onClick={() => patch({ status: 'completed' }, 'Task complete!')} disabled={busy}
              className="btn-primary px-5 py-2 rounded-full text-sm font-semibold" data-testid="complete-task-btn">
              Mark complete
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
